import { useState } from 'react';
import { Button, Text } from '@mantine/core';
import { clsx } from 'clsx';
import { useIntl } from 'react-intl';

import { FreightCarrierDetailsModal } from './freight-carrier-details-modal/FreightCarrierDetailsModal';
import { freightRateCardMessages } from './freight-rate-card-messages';
import styles from './FreightRateCard.module.css';
import { getCarrierInitials } from './get-carrier-initials';
import { getCarrierLogoSrc } from './get-carrier-logo-src';
import { RateSelector } from './RateSelector';

import type { CarrierRateRow } from './freight-rate-card-types';
import type { LtlAddress, LtlQuote } from '../../types/ltl';
import type { ShipFromAddress } from '../../types/ship-from';

export type FreightRateCardLayout = 'horizontal' | 'vertical';

type FreightRateCardProps = {
  carrierRow: CarrierRateRow;
  selectedOfferId?: string;
  onSelectRate: (quote: LtlQuote) => void;
  shipFromAddress: ShipFromAddress;
  destinationAddress: LtlAddress;
  /**
   * Arrangement of the carrier header and rate selectors.
   * - `vertical` (default): selectors stack below the header, each filling half the width (booking wizard).
   * - `horizontal`: selectors sit to the right of the header at a fixed width (rate browser).
   */
  layout?: FreightRateCardLayout;
};

export const FreightRateCard = ({
  carrierRow,
  selectedOfferId,
  onSelectRate,
  shipFromAddress,
  destinationAddress,
  layout = 'vertical',
}: FreightRateCardProps) => {
  const intl = useIntl();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const { carrierKey, carrierName, carrierScac, standard, guaranteed } = carrierRow;
  const isHorizontal = layout === 'horizontal';
  const logoSrc = getCarrierLogoSrc(carrierScac);

  return (
    <div className={clsx(styles.card, isHorizontal && styles.cardHorizontal)} data-testid={`freight-rate-card-${carrierKey}`}>
      <div className={styles.header}>
        <div className={styles.logoBox}>
          {logoSrc ? (
            <img alt={carrierName} className={styles.logoImage} src={logoSrc} />
          ) : (
            <span className={styles.logoInitials}>{getCarrierInitials(carrierName)}</span>
          )}
        </div>
        {isHorizontal ? (
          <div className={styles.carrierBlock}>
            <Text className={styles.carrierName} size='sm'>
              {carrierName}
            </Text>
            <Button onClick={() => setIsDetailsOpen(true)} variant='ghost'>
              {intl.formatMessage(freightRateCardMessages.viewDetails)}
            </Button>
          </div>
        ) : (
          <>
            <Text className={styles.carrierName} size='sm'>
              {carrierName}
            </Text>
            <Button onClick={() => setIsDetailsOpen(true)} variant='ghost'>
              {intl.formatMessage(freightRateCardMessages.viewDetails)}
            </Button>
          </>
        )}
      </div>
      <div className={clsx(styles.selectors, isHorizontal && styles.selectorsHorizontal)}>
        <RateSelector
          isSelected={!!standard && standard.offerId === selectedOfferId}
          label={intl.formatMessage(freightRateCardMessages.standardLabel)}
          onSelect={onSelectRate}
          quote={standard}
        />
        <RateSelector
          isSelected={!!guaranteed && guaranteed.offerId === selectedOfferId}
          label={intl.formatMessage(freightRateCardMessages.guaranteedLabel)}
          onSelect={onSelectRate}
          quote={guaranteed}
        />
      </div>
      <FreightCarrierDetailsModal
        carrierName={carrierName}
        carrierScac={carrierScac}
        destinationAddress={destinationAddress}
        onClose={() => setIsDetailsOpen(false)}
        opened={isDetailsOpen}
        quote={standard ?? guaranteed!}
        shipFromAddress={shipFromAddress}
      />
    </div>
  );
};
